import { useState } from 'react'
import { App } from 'antd'
import req from '@sportninja/common/api/request'
import { isMeaningfulOfficialEdit } from '@sportninja/common/features/competition/operations/utils/officialInlineEdit'
import type { EditableOfficialField } from '@sportninja/common/features/competition/operations/utils/officialInlineEdit'
import {
  applyOfficialInlineEdit,
  toOfficialUpdatePayload,
} from '@sportninja/common/features/competition/operations/utils/toOfficialUpdatePayload'
import type { RosterOfficialRow } from '@sportninja/common/features/competition/operations/utils/rosterRows'
import { useRosterContext } from 'src/pages/Team/UnifiedRoster/context/RosterContext'

interface Args {
  teamId: string
  rosterId: string
  /** Raw officials from the context, for fields the row shape drops. */
  rawOfficials: Array<Record<string, unknown>>
}

export interface InlineOfficialEdit {
  /** Commit one cell. Resolves once the row is back in sync with the server. */
  saveOfficialField: (
    row: RosterOfficialRow,
    field: EditableOfficialField,
    value: string,
    currentValue: string
  ) => Promise<void>
  /** Ids with a save in flight, for the pending row treatment. */
  pendingIds: Set<string>
}

/**
 * Saves a single Team Officials grid cell.
 *
 * Differs from `useInlinePlayerEdit` in one important way: the officials
 * endpoint takes a FULL payload, not a diff, so every field is resent on every
 * edit (see `toOfficialUpdatePayload`). It shares the player hook's other
 * constraint — `RosterContext` loads officials with a raw `req()`, not React
 * Query, so `refresh()` must be called explicitly or the edit appears to do
 * nothing until a reload.
 */
export const useInlineOfficialEdit = ({
  teamId,
  rosterId,
  rawOfficials,
}: Args): InlineOfficialEdit => {
  'use memo'

  const { message } = App.useApp()
  const { refresh } = useRosterContext()
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set())

  const setPending = (id: string, pending: boolean) =>
    setPendingIds((current) => {
      const next = new Set(current)
      if (pending) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })

  const saveOfficialField = async (
    row: RosterOfficialRow,
    field: EditableOfficialField,
    value: string,
    currentValue: string
  ) => {
    if (!isMeaningfulOfficialEdit(field, value, currentValue) || !row.id) {
      return
    }

    // `phone_number_country` is not carried on the row but the endpoint needs
    // it alongside a phone number, so it comes off the raw record.
    const raw = rawOfficials.find(
      (official) => String(official.id ?? '') === row.id
    )

    const body = toOfficialUpdatePayload(
      applyOfficialInlineEdit(
        {
          name: row.name,
          officialTypeId: row.officialTypeId,
          email: row.email,
          phone: row.phone,
          phoneCountry: raw?.phone_number_country
            ? String(raw.phone_number_country)
            : undefined,
        },
        field,
        value
      )
    )

    setPending(row.id, true)
    try {
      await req(
        `/teams/${teamId}/rosters/${rosterId}/team-officials/${row.id}`,
        {
          method: 'PUT',
          body: JSON.stringify(body),
        }
      )
      await refresh()
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : 'Could not save the change'
      )
      // Refresh on failure too, so the cell reverts to the server's value
      // instead of leaving the rejected edit on screen.
      await refresh()
    } finally {
      setPending(row.id, false)
    }
  }

  return { saveOfficialField, pendingIds }
}
